import { useSelector } from 'react-redux';
import { getItemAttributeByIdentifier } from '@configuratorware/configurator-frontendgui/App/Reducers/Configurator/AttributeSelectors';
import {
  getBlockedMaterialId,
  getOptionsForClassification,
  OptionClassificationIdentifiers,
} from '../../Reducers/Configurator/Selectors';
import { AttributeIdentifiers } from '../../Enums';
import { resolveItemColorOptionIds } from '../../utils/colorOptionResolve';
import { attrIsOn } from '../../utils/optionAttributes';
import type { EditorItem } from '../../Reducers/Configurator/types';

export type ItemColorConflict = {
  hasConflict: boolean;
};

function getAttrValues(option: unknown, attr: string): string[] {
  const values = (
    getItemAttributeByIdentifier(option, attr) as { values?: Array<{ value?: unknown }> } | undefined
  )?.values;
  if (!Array.isArray(values)) return [];
  return values
    .map((v) => (v?.value === undefined || v?.value === null ? '' : String(v.value)))
    .filter(Boolean);
}

export function useItemColorConflict(item: EditorItem): ItemColorConflict {
  const colorOptions = useSelector(getOptionsForClassification(OptionClassificationIdentifiers.color));
  const materialOptions = useSelector(getOptionsForClassification(OptionClassificationIdentifiers.material));
  const blockedMaterialId = useSelector(getBlockedMaterialId);

  const colorIds = resolveItemColorOptionIds(item, colorOptions);
  const itemColors = colorIds
    .map((id) => colorOptions.find((o) => (o as { identifier: string }).identifier === id))
    .filter(Boolean);

  const hasSurfaceConflict = itemColors.some(
    (o) => attrIsOn(o, 'matteOnly') || attrIsOn(o, 'glossOnly')
  );

  // Material conflicts are only badged while the material-section note is showing
  // (the user tried a blocked thickness), and only on the items whose colors block it.
  const blockedMaterial = blockedMaterialId
    ? materialOptions.find((mat) => (mat as { identifier: string }).identifier === blockedMaterialId)
    : null;
  const blockedThickness = blockedMaterial
    ? getAttrValues(blockedMaterial, AttributeIdentifiers.thickness)[0]
    : null;
  const hasMaterialConflict = Boolean(
    blockedThickness &&
      itemColors.some((c) => !getAttrValues(c, AttributeIdentifiers.thickness).includes(blockedThickness))
  );

  return { hasConflict: hasSurfaceConflict || hasMaterialConflict };
}
